import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiString as API_URL } from '../utils/constants';
import { AppThunk } from './store';

import { saveEngagementDraft } from './documentEngagement.slice';
import { copyObject } from '../utils/common';
import { SummaryNoteTypeEnum } from '../Enum/SummaryNoteTypeEnum';
import { gotoNextMenu, setAssessmentError } from './assessment.slice';
import { EatingDisorderModel } from '../Models/EatingDisorderModel/EatingDisorderModel.model';
import { preparePriorAssessmentForNewDraft } from '../utils/aiFlagHelpers';

const eatingDisorderSlice = createSlice({
    name: 'eatingDisorderSlice',
    initialState: { eatingDisorderInfo: new EatingDisorderModel(), isFetchingEatingDisorder: false },
    reducers: {
        _setIsFetchingEatingDisorder(state, action) {
            state.isFetchingEatingDisorder = action.payload;
        },
        _setEatingDisorderInfo(state, action) {
            state.eatingDisorderInfo = action.payload;
        },
    },
});

const { _setIsFetchingEatingDisorder, _setEatingDisorderInfo } = eatingDisorderSlice.actions;

export const setEatingDisorderInfo =
    (hamiltonAnxietyScale: EatingDisorderModel): AppThunk =>
    async (dispatch) => {
        dispatch(_setEatingDisorderInfo(hamiltonAnxietyScale));
    };

export const setIsFetchingEatingDisorder =
    (isFetching: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setIsFetchingEatingDisorder(isFetching));
    };

export const saveEatingDisorderInfo =
    (eatingDisorderModel: EatingDisorderModel, _saveLocal = false, gotoNext = false): AppThunk<Promise<boolean>> =>
    async (dispatch) => {
        const isUpdate = Boolean(eatingDisorderModel.Id);
        const url = isUpdate ? `${API_URL}/eatingdisorder/updateeatingdisorder/` : `${API_URL}/eatingdisorder/inserteatingdisorder/`;

        try {
            const response = await axios.put(url, eatingDisorderModel);
            if (Boolean(response.data)) {
                dispatch(setEatingDisorderInfo(isUpdate ? eatingDisorderModel : { ...eatingDisorderModel, Id: response.data.Id }));
            }
            const isDraftSaved = await dispatch(prepareEatingDisorderNotes(isUpdate ? eatingDisorderModel : response.data));
            if (gotoNext && isDraftSaved) {
                dispatch(gotoNextMenu());
            }
            return isDraftSaved;
        } catch (err) {
            dispatch(setAssessmentError({ Show: true, Message: err?.response?.data }));
            return false;
        }
    };

const prepareEatingDisorderNotes =
    (eatingDisorderInfo: EatingDisorderModel): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        if (Boolean(eatingDisorderInfo.Id)) {
            let engagementDraft = copyObject(getState().documentEngagement.engagementDraft);
            let memberAssessmentHistory = copyObject(getState().documentEngagement.memberAssessmentHistory || {});
            let summaryNotes = (engagementDraft.SummaryNotes || []).filter((s) => s.Type !== SummaryNoteTypeEnum.EatingDisorder);
            summaryNotes.push({
                Id: eatingDisorderInfo.MemberId,
                CptCode: '',
                Zcode: '',
                Note: 'Eating Disorder Assessment Completed',
                Type: SummaryNoteTypeEnum.EatingDisorder,
                IsConfirm: true,
                DisplayName: 'Eating Disorder',
            });
            engagementDraft.SummaryNotes = summaryNotes;
            const updatedMemberAssessmentStatuses = {
                ...memberAssessmentHistory.MemberAssessmentStatuses,
                EatingDisorderStatus: eatingDisorderInfo.AssessmentStatus,
            };
            const updatedEngagementDraft = {
                ...engagementDraft,
                EatingDisorderId: eatingDisorderInfo.Id,
            };
            const updatedMemberAssessmentHistory = {
                ...memberAssessmentHistory,
                MemberAssessmentStatuses: updatedMemberAssessmentStatuses,
                EngagementDraft: updatedEngagementDraft,
            };
            return dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
        }
        return false;
    };

export const fetchEatingDisorderInfo =
    (id: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsFetchingEatingDisorder(true));
        const response = await axios.get(`${API_URL}/eatingdisorder/fetcheatingdisorderbyid?id=${id}`);
        dispatch(setEatingDisorderInfo(response.data));
        dispatch(setIsFetchingEatingDisorder(false));
    };

export const fetchEatingDisorderInfoByMemberId =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsFetchingEatingDisorder(true));
        const response = await axios.get(`${API_URL}/eatingdisorder/fetcheatingdisorderbymemberid?memberId=${memberId}`);
        if (response.data) {
            preparePriorAssessmentForNewDraft(response.data);
        }
        dispatch(setEatingDisorderInfo(response.data));
        dispatch(setIsFetchingEatingDisorder(false));
    };

export default eatingDisorderSlice.reducer;
